import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';

import { brooksBrandProfile, getCompanyBrandCssVars } from '../../branding/companyBrand';

const routeExamples = [
  {
    label: 'Review request',
    previewPath: '/preview/review-request',
    value: `${brooksBrandProfile.domain}/code`,
  },
  {
    label: 'Referral request',
    previewPath: '/preview/referral-request',
    value: `${brooksBrandProfile.domain}/referral`,
  },
  {
    label: 'Referral dashboard',
    previewPath: '/preview/referral-auth',
    value: `${brooksBrandProfile.domain}/rewards`,
  },
  {
    label: 'Referral landing form',
    previewPath: '/preview/referral-landing',
    value: `${brooksBrandProfile.domain}/r/austin`,
  },
  {
    label: 'Tip checkout',
    previewPath: '/preview/tip-checkout',
    value: `${brooksBrandProfile.domain}/tip`,
  },
];

const setupItems = [
  'Tenant subdomain routing',
  'Mock CX API customization',
  'Config-driven screen renderer',
  'Composable CX components',
  'Stripe-hosted tip checkout',
];

const RatingsDashboard: React.FC = () => {
  return (
    <Box
      style={getCompanyBrandCssVars(brooksBrandProfile)}
      sx={{
        bgcolor: 'var(--color-gray-50)',
        color: 'var(--color-gray-900)',
        minHeight: '100vh',
        px: { xs: 2, md: 4 },
        py: { xs: 3, md: 5 },
      }}
    >
      <Stack spacing={3} sx={{ maxWidth: 1120, mx: 'auto' }}>
        <Box
          sx={{
            alignItems: { xs: 'flex-start', sm: 'center' },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            justifyContent: 'space-between',
          }}
        >
          <Stack spacing={1} sx={{ maxWidth: 620 }}>
            <Chip
              color="primary"
              label="cx-client"
              size="small"
              sx={{ alignSelf: 'flex-start', fontWeight: 700 }}
            />
            <Typography
              variant="h3"
              sx={{
                fontFamily: 'Barlow, "Public Sans Variable", Arial, sans-serif',
                fontSize: { xs: 34, md: 44 },
                fontWeight: 800,
                lineHeight: 1.15,
              }}
            >
              Customer experience client
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: 16, lineHeight: 1.6 }}>
              Standalone shell for review requests, referrals, and tip collection.
            </Typography>
          </Stack>

          <Button
            component={RouterLink}
            endIcon={<ArrowForwardRoundedIcon />}
            to="/preview/referral-request"
            variant="contained"
            sx={{
              bgcolor: 'var(--cx-brand-primary)',
              color: 'var(--color-base-white)',
              '&:hover': { bgcolor: 'var(--cx-brand-primary-dark)' },
            }}
          >
            Preview referral flow
          </Button>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.8fr' },
          }}
        >
          <Card sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack spacing={2.5}>
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.25 }}>
                <RateReviewRoundedIcon sx={{ color: 'var(--cx-brand-primary)' }} />
                <Typography variant="h6">Company-branded request routes</Typography>
              </Box>

              <Stack spacing={1.25}>
                {routeExamples.map((route) => (
                  <Box
                    aria-label={`Preview ${route.label}`}
                    component={RouterLink}
                    key={route.value}
                    to={route.previewPath}
                    sx={{
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      bgcolor: 'var(--color-gray-50)',
                      border: '1px solid',
                      borderColor: 'var(--color-gray-200)',
                      borderRadius: 2,
                      color: 'inherit',
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 0.75,
                      justifyContent: 'space-between',
                      px: 1.5,
                      py: 1.25,
                      textDecoration: 'none',
                      transition: 'border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease',
                      '&:hover': {
                        bgcolor: 'var(--color-base-white)',
                        borderColor: 'var(--cx-brand-primary)',
                        boxShadow: '0 0 0 3px color-mix(in srgb, var(--cx-brand-primary) 12%, transparent)',
                      },
                    }}
                  >
                    <Typography variant="body2" sx={{ color: 'var(--color-gray-900)', fontWeight: 700 }}>
                      {route.label}
                    </Typography>
                    <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          bgcolor: 'var(--color-base-white)',
                          border: '1px solid',
                          borderColor: 'var(--color-gray-200)',
                          borderRadius: 1,
                          color: 'var(--color-gray-900)',
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                          px: 1,
                          py: 0.5,
                        }}
                      >
                        {route.value}
                      </Typography>
                      <Chip
                        icon={<OpenInNewRoundedIcon />}
                        label="Preview"
                        size="small"
                        sx={{
                          bgcolor: 'var(--cx-brand-primary-soft)',
                          color: 'var(--cx-brand-primary-dark)',
                          fontWeight: 800,
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Card>

          <Card sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack divider={<Divider flexItem />} spacing={2}>
              <Stack spacing={1}>
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.25 }}>
                  <TuneRoundedIcon sx={{ color: 'var(--cx-brand-primary)' }} />
                  <Typography variant="h6">Bones in place</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Ready for CX Studio to drive copy, brand, and request flow.
                </Typography>
              </Stack>

              <Stack spacing={1}>
                {setupItems.map((item) => (
                  <Box key={item} sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                    <VerifiedRoundedIcon sx={{ color: 'var(--cx-brand-primary)', fontSize: 19 }} />
                    <Typography variant="body2">{item}</Typography>
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Card>
        </Box>

        <Card sx={{ p: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              alignItems: { xs: 'flex-start', sm: 'center' },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.25 }}>
              <PaymentsRoundedIcon sx={{ color: 'var(--cx-brand-primary)' }} />
              <Box>
                <Typography variant="h6">Stripe tipping</Typography>
                <Typography variant="body2" color="text.secondary">
                  Card and wallet checkout plus Cash App Pay are routed through Stripe.
                </Typography>
              </Box>
            </Box>

            <Button
              component={RouterLink}
              endIcon={<ArrowForwardRoundedIcon />}
              to="/preview/tip-checkout"
              variant="outlined"
              sx={{
                borderColor: 'var(--cx-brand-primary)',
                color: 'var(--cx-brand-primary-dark)',
                '&:hover': {
                  bgcolor: 'var(--cx-brand-primary-soft)',
                  borderColor: 'var(--cx-brand-primary)',
                },
              }}
            >
              Open preview
            </Button>
          </Box>
        </Card>

        <Card sx={{ p: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              alignItems: { xs: 'flex-start', sm: 'center' },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.25 }}>
              <PersonAddAltRoundedIcon sx={{ color: 'var(--cx-brand-primary)' }} />
              <Box>
                <Typography variant="h6">User referral dashboard</Typography>
                <Typography variant="body2" color="text.secondary">
                  Phone or email auth into referral links, rewards, payout status, and activity.
                </Typography>
              </Box>
            </Box>

            <Button
              component={RouterLink}
              endIcon={<ArrowForwardRoundedIcon />}
              to="/preview/referral-auth"
              variant="outlined"
              sx={{
                borderColor: 'var(--cx-brand-primary)',
                color: 'var(--cx-brand-primary-dark)',
                flexShrink: 0,
                '&:hover': {
                  bgcolor: 'var(--cx-brand-primary-soft)',
                  borderColor: 'var(--cx-brand-primary)',
                },
              }}
            >
              Open auth
            </Button>
          </Box>
        </Card>

        <Card sx={{ p: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              alignItems: { xs: 'flex-start', sm: 'center' },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.25 }}>
              <RateReviewRoundedIcon sx={{ color: 'var(--cx-brand-primary)' }} />
              <Box>
                <Typography variant="h6">Referral landing form</Typography>
                <Typography variant="body2" color="text.secondary">
                  Public referral destination for referred customers to claim an offer and request service.
                </Typography>
              </Box>
            </Box>

            <Button
              component={RouterLink}
              endIcon={<ArrowForwardRoundedIcon />}
              to="/preview/referral-landing"
              variant="outlined"
              sx={{
                borderColor: 'var(--cx-brand-primary)',
                color: 'var(--cx-brand-primary-dark)',
                flexShrink: 0,
                '&:hover': {
                  bgcolor: 'var(--cx-brand-primary-soft)',
                  borderColor: 'var(--cx-brand-primary)',
                },
              }}
            >
              Open landing form
            </Button>
          </Box>
        </Card>

        <Card sx={{ p: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              alignItems: { xs: 'flex-start', sm: 'center' },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.25 }}>
              <AccountTreeRoundedIcon sx={{ color: 'var(--cx-brand-primary)' }} />
              <Box>
                <Typography variant="h6">Configurable flow renderer</Typography>
                <Typography variant="body2" color="text.secondary">
                  Screens are composed from typed blocks like headings, media, feedback, NPS, CSAT, reviews, referrals, and tipping.
                </Typography>
              </Box>
            </Box>

            <Button
              component={RouterLink}
              endIcon={<ArrowForwardRoundedIcon />}
              to="/preview/configurable-flow"
              variant="contained"
              sx={{
                bgcolor: 'var(--cx-brand-primary)',
                color: 'var(--color-base-white)',
                flexShrink: 0,
                '&:hover': { bgcolor: 'var(--cx-brand-primary-dark)' },
              }}
            >
              Open flow preview
            </Button>
          </Box>
        </Card>
      </Stack>
    </Box>
  );
};

export default RatingsDashboard;
